import {Calculation, Unit, Language, Translations, IncomeTypeMetadata, IncomeTypesLogic, CalcRowsLogic, IrRow, CalculationRowType, EnumerationsLogic} from "@salaxy/core";
/**
 * Helper class used to create IrRowsReport config for sxy-table and create tableData for ExcelHelpers(ng1)
 */
export class IrRowsReportConfigGenerator{
  /**
   * Generates config for SxyTable component
   * @param calcs Calculations for report
   * @param lang Language for report
   * @returns Configuration for SxyTable component
   */
  public static getConfig(calcs: Calculation[], lang: Language):{ options: any, include: any[], data: any[]} {
    const getData = (): any[] => {
      const result: any[] = [];
        calcs.forEach(calc => {
            const irRows: IrRow[] = calc.result.irRows;
            irRows.forEach(row => {
              result.push({
                calc,
                irCode: row.irData.code,
                irCodeName: this.getIncomeType(row, lang).label,
                worker: calc.worker.avatar.displayName,
                message: row.message,
                rowType: this.translateRowType(row, lang),
                count: row.count,
                price: row.price,
                unit: row?.unit === Unit.One ? "" : CalcRowsLogic.getUnitIndicator(row.unit, lang),
                total: row.total
              });
            });
        });
        return result;
    };
    const getConfig =  ():{ options: any, include: any[], data: any[]}  => {
      return {
        include: [
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.worker", null, lang), field: "worker", frozen: true, minWidth: 100},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.irCode", null, lang), field: "irCode"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.incomeType", null, lang), field: "irCodeName"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.rowType", null, lang), field: "rowType"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.message", null, lang), field: "message"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.count", null, lang), field: "count"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.price", null, lang), field: "price", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.unit", null, lang), field: "unit"},
          {title: Translations.get("SALAXY.REPORTS.calcsIrRowsReport.total", null, lang), field: "total", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }}
        ],
        options: {
          selectable: false,
          layout: "fitColumns"
        },
        data: getData()
      };
    };
    return getConfig();
  }

  /** Generates tableData for ExcelHelpers.export (ng1) */
  public static getTableData(calcs: Calculation[], lang: Language): any[] {
    const result = [];
    const headers = [
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.worker", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.irCode", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.incomeType", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.rowType", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.message", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.count", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.price", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.unit", null, lang),
      Translations.get("SALAXY.REPORTS.calcsIrRowsReport.total", null, lang)
    ];
    result.push(headers);
    calcs.forEach(calc => {
      calc?.result?.irRows?.forEach(irRow => {
        const row = [];
        row.push(calc.worker.avatar.displayName);
        row.push(irRow?.irData?.code);
        row.push(this.getIncomeType(irRow, lang).label);
        row.push(irRow.message);
        row.push(this.translateRowType(irRow, lang));
        row.push(irRow.count);
        row.push(irRow.price);
        row.push(irRow?.unit === Unit.One ? "" : CalcRowsLogic.getUnitIndicator(irRow.unit, lang));
        row.push(irRow.total);
        result.push(row);
      });
    });
    return result;
  }

  private static getIncomeType(row: any, lang: Language): IncomeTypeMetadata {
    return IncomeTypesLogic.getAll(lang).find((x) => x.code === row.irData.code);
  }

  private static translateRowType(row: IrRow, lang: Language): string {
    let rowType = row.data.calculationRowType || CalculationRowType.Unknown;
    rowType = (rowType.substr(0, 1).toLowerCase() + rowType.substr(1)) as CalculationRowType;
    const code = row.irData?.code as number;
    const kind = row.data.kind as string;

    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(lang).find( (x) => (kind && x.transactionCode === kind) || (code > 0 && x.code === code))?.label;
    } else if (rowType === CalculationRowType.MealBenefit && code !== 334) {
      // Meal benefit deductions
      label = IncomeTypesLogic.getAll(lang).find( (x) => code > 0 && x.code === code)?.label;
    } else if([
      CalculationRowType.Unknown,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.TaxAtSource,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation
    ].includes(rowType))
    {
      label = IncomeTypesLogic.getAll(lang).find( (x) => code > 0 && x.code === code)?.label;
    }
    else if([
      CalculationRowType.DailyAllowance,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.MealBenefit, // main MealBenefit
      CalculationRowType.Training,
      CalculationRowType.PaidSickLeave,
    ].includes(rowType) && kind) {
      label =  EnumerationsLogic.getEnumLabel(`${row.data.calculationRowType}Kind`, kind, null, lang);
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, lang);
  }
}